import { useEffect, useState } from 'react';
import cronstrue from 'cronstrue';
import { AlertTriangle, Bot, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';
import { cn } from '@/lib/utils';

type TriggerType = 'manual' | 'cron' | 'webhook';

type Trigger =
  | { type: 'manual' }
  | {
      type: 'cron';
      cron: { spec: string; timezone: string; coalesce_n: number; skip_alert_m: number };
    }
  | {
      type: 'webhook';
      webhook: { secret: string; coalesce_n: number; skip_alert_m: number };
    };

type HookEvent =
  | 'PreToolUse'
  | 'PostToolUse'
  | 'UserPromptSubmit'
  | 'Stop'
  | 'SubagentStop'
  | 'SessionStart'
  | 'SessionEnd';

interface HookConfig {
  event: HookEvent;
  template_key: string;
  params: Record<string, unknown>;
}

interface ProjectRef {
  id: string;
  name: string;
  productId?: string | null;
  slug?: string;
}

interface Agent {
  id: string;
  name: string;
  description: string;
  sandboxId: string | null;
  projectId: string | null;
  primarySkillId?: string | null;
  additionalSkillIds: string[];
  subagentIds: string[];
  mcpIds: string[];
  allowedBuiltinTools: string[];
  model: string;
  permissionMode: 'autoAccept' | 'askUser';
  cwd: string;
  trigger: Trigger;
  hooks: HookConfig[];
  inlineSystemPrompt?: string | null;
}

const HOOK_EVENTS: HookEvent[] = [
  'PreToolUse',
  'PostToolUse',
  'UserPromptSubmit',
  'Stop',
  'SubagentStop',
  'SessionStart',
  'SessionEnd',
];

interface HookTemplate {
  key: string;
  label: string;
  defaultEvent: HookEvent;
  description: string;
  defaultParams: Record<string, unknown>;
}

const HOOK_TEMPLATES: HookTemplate[] = [
  {
    key: 'block_writes_outside_cwd',
    label: 'Block writes outside cwd',
    defaultEvent: 'PreToolUse',
    description: 'Refuses Write/Edit/MultiEdit whose target escapes the agent cwd.',
    defaultParams: {},
  },
  {
    key: 'block_secret_files',
    label: 'Block secret files',
    defaultEvent: 'PreToolUse',
    description: 'Refuses Read/Glob/Grep on paths matching any of the configured patterns.',
    defaultParams: { globs: ['.env', '.env.local', '.npmrc', '.aws/credentials'] },
  },
  {
    key: 'require_approval_for_bash',
    label: 'Require approval for Bash',
    defaultEvent: 'PreToolUse',
    description: "Forces every Bash invocation through canUseTool, even in autoAccept mode.",
    defaultParams: {},
  },
  {
    key: 'log_tool_use',
    label: 'Log tool use',
    defaultEvent: 'PostToolUse',
    description: 'Emits a chat-visible system message after every tool call.',
    defaultParams: {},
  },
  {
    key: 'notify_on_session_end',
    label: 'Notify on session end',
    defaultEvent: 'SessionEnd',
    description: 'Posts a system message when the session ends.',
    defaultParams: { level: 'info' },
  },
];

interface Sandbox {
  id: string;
  name: string;
  status: string;
  workingDirs?: string[];
}

interface SkillRef {
  id: string;
  name: string;
  description: string;
  files?: Record<string, unknown>;
}

interface McpRef {
  id: string;
  name: string;
  description: string;
  kind: 'stdio' | 'http' | 'sse';
}

/**
 * Card-style multi-select used for both Skills and MCPs in the AgentDialog.
 * Each row toggles via click anywhere on the card; checkbox is visual only.
 */
function PickerList<T extends { id: string }>({
  items,
  selectedIds,
  onToggle,
  renderRow,
  empty,
}: {
  items: T[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  renderRow: (item: T) => React.ReactNode;
  empty: React.ReactNode;
}) {
  if (items.length === 0) {
    return <p className="text-xs text-muted-foreground">{empty}</p>;
  }
  return (
    <div className="space-y-1.5">
      {items.map((it) => {
        const selected = selectedIds.includes(it.id);
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onToggle(it.id)}
            className={cn(
              'flex w-full items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
              selected
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-foreground/30 hover:bg-accent/40',
            )}
          >
            <Checkbox checked={selected} className="pointer-events-none shrink-0" />
            <div className="min-w-0 flex-1">{renderRow(it)}</div>
          </button>
        );
      })}
    </div>
  );
}

interface AgentsViewProps {
  onOpenSession: (sessionId: string) => void;
}

export function AgentsView({ onOpenSession }: AgentsViewProps) {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [sandboxes, setSandboxes] = useState<Sandbox[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Agent | 'new' | null>(null);
  const [starting, setStarting] = useState<Agent | null>(null);

  const reload = async () => {
    try {
      const [a, s] = await Promise.all([
        api.get<Agent[]>('/api/agents'),
        api.get<Sandbox[]>('/api/sandboxes'),
      ]);
      setAgents(a);
      setSandboxes(s);
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, []);

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title="Agents"
        description="Reusable agent templates. Start a session from any online sandbox."
        action={
          <Button size="sm" disabled={sandboxes.length === 0} onClick={() => setEditing('new')}>
            <Plus />
            New
          </Button>
        }
      />

      <ItemGroupContent>
        {loading && agents.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && agents.length === 0 && <ItemGroupEmpty>no agents yet</ItemGroupEmpty>}

        {agents.map((a) => {
          const sb = a.sandboxId ? sandboxes.find((s) => s.id === a.sandboxId) : undefined;
          const orphaned = !a.sandboxId;
          return (
            <Item key={a.id}>
              <ItemMedia>
                <Bot />
              </ItemMedia>
              <ItemContent>
                <ItemHeader>
                  <ItemTitle>{a.name}</ItemTitle>
                  <Badge variant="secondary" className="text-[10px]">
                    {a.model}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {a.permissionMode}
                  </Badge>
                  <Badge variant="outline" className="text-[10px]">
                    {a.trigger.type}
                  </Badge>
                </ItemHeader>
                {orphaned ? (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                    <AlertTriangle className="size-3.5 text-amber-500" />
                    <span className="text-amber-500">no sandbox attached</span>
                    <Select
                      onValueChange={async (newId) => {
                        try {
                          await api.patch(`/api/agents/${a.id}`, { sandbox_id: newId });
                          void reload();
                        } catch (err) {
                          void showAlert((err as { message: string }).message);
                        }
                      }}
                    >
                      <SelectTrigger className="h-7 max-w-[200px] text-xs">
                        <SelectValue placeholder="attach to…" />
                      </SelectTrigger>
                      <SelectContent>
                        {sandboxes.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <ItemDescription>sandbox: {sb?.name ?? a.sandboxId}</ItemDescription>
                )}
                {a.inlineSystemPrompt && (
                  <code className="mt-1 block rounded bg-muted px-2 py-1 text-[11px] text-muted-foreground">
                    {a.inlineSystemPrompt.slice(0, 140)}
                    {a.inlineSystemPrompt.length > 140 ? '…' : ''}
                  </code>
                )}
              </ItemContent>
              <ItemActions>
                <Button
                  size="sm"
                  disabled={orphaned || sb?.status !== 'online'}
                  onClick={() => setStarting(a)}
                >
                  <Play />
                  Start
                </Button>
                <Button variant="outline" size="icon" onClick={() => setEditing(a)}>
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={async () => {
                    if (!(await showConfirm(`Delete agent "${a.name}"?`))) return;
                    try {
                      await api.del(`/api/agents/${a.id}`);
                      void reload();
                    } catch (e) {
                      void showAlert((e as { message: string }).message);
                    }
                  }}
                >
                  <Trash2 />
                </Button>
              </ItemActions>
            </Item>
          );
        })}
      </ItemGroupContent>

      <AgentDialog
        open={editing !== null}
        initial={editing === 'new' ? null : (editing as Agent | null)}
        sandboxes={sandboxes}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void reload();
        }}
      />

      <StartAgentDialog
        agent={starting}
        onOpenChange={(open) => !open && setStarting(null)}
        onStarted={(sessionId) => {
          setStarting(null);
          onOpenSession(sessionId);
        }}
      />
    </ItemGroup>
  );
}

/* ----------------------------------------------------------------- AgentDialog */

function newWebhookSecret(): string {
  return Array.from(crypto.getRandomValues(new Uint8Array(24)))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function defaultTrigger(type: TriggerType): Trigger {
  if (type === 'cron') {
    return {
      type: 'cron',
      cron: { spec: '0 * * * *', timezone: 'UTC', coalesce_n: 1, skip_alert_m: 5 },
    };
  }
  if (type === 'webhook') {
    return {
      type: 'webhook',
      webhook: { secret: newWebhookSecret(), coalesce_n: 1, skip_alert_m: 5 },
    };
  }
  return { type: 'manual' };
}

function AgentDialog({
  open,
  initial,
  sandboxes,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  initial: Agent | null;
  sandboxes: Sandbox[];
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [sandboxId, setSandboxId] = useState('');
  const [model, setModel] = useState('claude-haiku-4-5');
  const [overridePrompt, setOverridePrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [cwd, setCwd] = useState('/tmp/control-agent');
  const [permMode, setPermMode] = useState<'autoAccept' | 'askUser'>('askUser');
  const [skills, setSkills] = useState<SkillRef[]>([]);
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [mcps, setMcps] = useState<McpRef[]>([]);
  const [mcpIds, setMcpIds] = useState<string[]>([]);
  const [projects, setProjects] = useState<ProjectRef[]>([]);
  const [projectId, setProjectId] = useState<string>('');
  const [trigger, setTrigger] = useState<Trigger>({ type: 'manual' });
  const [hooks, setHooks] = useState<HookConfig[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name);
      setSandboxId(initial.sandboxId ?? sandboxes[0]?.id ?? '');
      setModel(initial.model);
      setOverridePrompt(Boolean(initial.inlineSystemPrompt));
      setPrompt(initial.inlineSystemPrompt ?? '');
      setCwd(initial.cwd);
      setPermMode(initial.permissionMode);
      // Merge any legacy primary_skill_id into the unified list.
      const initialSkills = new Set(initial.additionalSkillIds ?? []);
      if (initial.primarySkillId) initialSkills.add(initial.primarySkillId);
      setSkillIds([...initialSkills]);
      setMcpIds(initial.mcpIds ?? []);
      setProjectId(initial.projectId ?? '');
      setTrigger(initial.trigger);
      setHooks(initial.hooks ?? []);
    } else {
      setName('');
      setSandboxId(sandboxes[0]?.id ?? '');
      setModel('claude-haiku-4-5');
      setOverridePrompt(false);
      setPrompt('');
      setCwd('/tmp/control-agent');
      setPermMode('askUser');
      setSkillIds([]);
      setMcpIds([]);
      setProjectId('');
      setTrigger({ type: 'manual' });
      setHooks([]);
    }
    setErr(null);
    void api.get<SkillRef[]>('/api/skills').then(setSkills).catch(() => undefined);
    void api.get<McpRef[]>('/api/mcps').then(setMcps).catch(() => undefined);
    void api.get<ProjectRef[]>('/api/projects').then(setProjects).catch(() => undefined);
  }, [open, initial, sandboxes]);

  const toggleSkill = (id: string): void => {
    setSkillIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleMcp = (id: string): void => {
    setMcpIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const body = {
        name,
        sandbox_id: sandboxId,
        model,
        cwd,
        permission_mode: permMode,
        ...(overridePrompt && prompt.trim() ? { inline_system_prompt: prompt } : {}),
        // Drop legacy primary_skill_id; the unified picker maps to
        // additional_skill_ids only.
        primary_skill_id: null,
        additional_skill_ids: skillIds,
        mcp_ids: mcpIds,
        project_id: projectId || null,
        trigger,
        hooks,
      };
      if (initial) {
        await api.patch(`/api/agents/${initial.id}`, body);
      } else {
        await api.post('/api/agents', body);
      }
      onSaved();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{initial ? `Edit "${initial.name}"` : 'New agent'}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="ag-name">Name</Label>
            <Input id="ag-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Sandbox</Label>
            <Select value={sandboxId} onValueChange={setSandboxId}>
              <SelectTrigger>
                <SelectValue placeholder="Select sandbox" />
              </SelectTrigger>
              <SelectContent>
                {sandboxes.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Model</Label>
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="claude-opus-4-7">claude-opus-4-7</SelectItem>
                <SelectItem value="claude-opus-4-6">claude-opus-4-6</SelectItem>
                <SelectItem value="claude-sonnet-4-6">claude-sonnet-4-6</SelectItem>
                <SelectItem value="claude-haiku-4-5">claude-haiku-4-5</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Permission mode</Label>
            <Select value={permMode} onValueChange={(v) => setPermMode(v as 'autoAccept' | 'askUser')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="askUser">askUser</SelectItem>
                <SelectItem value="autoAccept">autoAccept</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <CwdField
            sandboxId={sandboxId}
            sandboxes={sandboxes}
            value={cwd}
            onChange={setCwd}
          />

          <div className="space-y-1.5">
            <Label>Project (optional)</Label>
            <Select
              value={projectId || 'none'}
              onValueChange={(v) => setProjectId(v === 'none' ? '' : v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">(none)</SelectItem>
                {projects.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              When linked, the project's PRD/TechSpec and the parent product's ARCH are appended to
              the system prompt at session start.
            </p>
          </div>

          <div className="space-y-2 rounded-md border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label htmlFor="ag-override" className="cursor-pointer">
                  Override Claude Code system prompt
                </Label>
                <p className="text-xs text-muted-foreground">
                  Off: agent uses Claude Code's full default prompt. On: provide your own.
                </p>
              </div>
              <Switch
                id="ag-override"
                checked={overridePrompt}
                onCheckedChange={(v) => setOverridePrompt(v)}
              />
            </div>
            {overridePrompt && (
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="You are a helpful assistant…"
                className="min-h-[120px]"
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label>Skills</Label>
            <PickerList
              items={skills}
              selectedIds={skillIds}
              onToggle={toggleSkill}
              empty="No skills yet — create one in the Skills tab."
              renderRow={(s) => {
                const fileCount = Object.keys(s.files ?? {}).length;
                return (
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-medium">{s.name}</span>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">
                        {fileCount} file{fileCount === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    {s.description && (
                      <p className="truncate text-xs text-muted-foreground">{s.description}</p>
                    )}
                  </div>
                );
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label>MCPs</Label>
            <PickerList
              items={mcps}
              selectedIds={mcpIds}
              onToggle={toggleMcp}
              empty="No MCPs yet — create one in the MCPs tab."
              renderRow={(m) => (
                <div className="min-w-0 space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{m.name}</span>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {m.kind}
                    </Badge>
                  </div>
                  {m.description && (
                    <p className="truncate text-xs text-muted-foreground">{m.description}</p>
                  )}
                </div>
              )}
            />
          </div>

          <TriggerEditor
            agentId={initial?.id ?? null}
            trigger={trigger}
            onChange={setTrigger}
          />

          <HooksEditor hooks={hooks} onChange={setHooks} />

          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !name || !sandboxId} onClick={save}>
            {initial ? 'Save' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* --------------------------------------------------------------- TriggerEditor */

function TriggerEditor({
  agentId,
  trigger,
  onChange,
}: {
  agentId: string | null;
  trigger: Trigger;
  onChange: (t: Trigger) => void;
}) {
  const setType = (type: TriggerType) => {
    if (type === trigger.type) return;
    onChange(defaultTrigger(type));
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="space-y-1.5">
        <Label>Trigger</Label>
        <Select value={trigger.type} onValueChange={(v) => setType(v as TriggerType)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">manual — start from MiniApp / API</SelectItem>
            <SelectItem value="cron">cron — schedule</SelectItem>
            <SelectItem value="webhook">webhook — POST to URL</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {trigger.type === 'cron' && (
        <CronFields
          value={trigger.cron}
          onChange={(cron) => onChange({ type: 'cron', cron })}
        />
      )}
      {trigger.type === 'webhook' && (
        <WebhookFields
          agentId={agentId}
          value={trigger.webhook}
          onChange={(webhook) => onChange({ type: 'webhook', webhook })}
        />
      )}
    </div>
  );
}

function describeCron(spec: string): { text: string; ok: boolean } {
  try {
    return { text: cronstrue.toString(spec, { use24HourTimeFormat: true }), ok: true };
  } catch (e) {
    return { text: (e as Error).message, ok: false };
  }
}

function CronFields({
  value,
  onChange,
}: {
  value: { spec: string; timezone: string; coalesce_n: number; skip_alert_m: number };
  onChange: (v: typeof value) => void;
}) {
  const desc = describeCron(value.spec);
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1.5">
        <Label htmlFor="cron-spec">Crontab</Label>
        <Input
          id="cron-spec"
          value={value.spec}
          onChange={(e) => onChange({ ...value, spec: e.target.value })}
          className="font-mono text-xs"
          placeholder="0 * * * *"
        />
        <p
          className={cn(
            'text-[11px]',
            desc.ok ? 'text-muted-foreground' : 'text-destructive',
          )}
        >
          {desc.text}
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cron-tz">Timezone</Label>
        <Input
          id="cron-tz"
          value={value.timezone}
          onChange={(e) => onChange({ ...value, timezone: e.target.value })}
          className="font-mono text-xs"
          placeholder="UTC"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cron-coalesce">Coalesce after</Label>
        <Input
          id="cron-coalesce"
          type="number"
          min={0}
          value={value.coalesce_n}
          onChange={(e) => onChange({ ...value, coalesce_n: Number(e.target.value) })}
        />
        <p className="text-[11px] text-muted-foreground">
          If ≤ this many fires queued while offline, fire one on reconnect.
        </p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cron-skip">Skip + alert above</Label>
        <Input
          id="cron-skip"
          type="number"
          min={0}
          value={value.skip_alert_m}
          onChange={(e) => onChange({ ...value, skip_alert_m: Number(e.target.value) })}
        />
        <p className="text-[11px] text-muted-foreground">
          If more queued than this, drop them all and post an alert.
        </p>
      </div>
    </div>
  );
}

function WebhookFields({
  agentId,
  value,
  onChange,
}: {
  agentId: string | null;
  value: { secret: string; coalesce_n: number; skip_alert_m: number };
  onChange: (v: typeof value) => void;
}) {
  const url = agentId
    ? `${location.origin}/hooks/agents/${agentId}?secret=${value.secret}`
    : 'Save the agent first to see the URL';
  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <Label htmlFor="wh-secret">Secret</Label>
        <div className="flex gap-2">
          <Input
            id="wh-secret"
            value={value.secret}
            onChange={(e) => onChange({ ...value, secret: e.target.value })}
            className="font-mono text-xs"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => onChange({ ...value, secret: newWebhookSecret() })}
          >
            Regenerate
          </Button>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Endpoint URL</Label>
        <pre className="overflow-x-auto rounded border bg-muted p-2 text-[11px]">{url}</pre>
        {agentId && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigator.clipboard?.writeText(url)}
          >
            Copy URL
          </Button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1.5">
          <Label htmlFor="wh-coalesce">Coalesce after</Label>
          <Input
            id="wh-coalesce"
            type="number"
            min={0}
            value={value.coalesce_n}
            onChange={(e) => onChange({ ...value, coalesce_n: Number(e.target.value) })}
          />
          <p className="text-[11px] text-muted-foreground">
            If ≤ this many fires queued while offline, fire one on reconnect.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="wh-skip">Skip + alert above</Label>
          <Input
            id="wh-skip"
            type="number"
            min={0}
            value={value.skip_alert_m}
            onChange={(e) => onChange({ ...value, skip_alert_m: Number(e.target.value) })}
          />
          <p className="text-[11px] text-muted-foreground">
            If more queued than this, drop them all and post an alert.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- HooksEditor */

function HooksEditor({
  hooks,
  onChange,
}: {
  hooks: HookConfig[];
  onChange: (next: HookConfig[]) => void;
}) {
  const add = () => {
    const tpl = HOOK_TEMPLATES[0]!;
    onChange([
      ...hooks,
      {
        event: tpl.defaultEvent,
        template_key: tpl.key,
        params: { ...tpl.defaultParams },
      },
    ]);
  };

  const update = (i: number, patch: Partial<HookConfig>) => {
    onChange(hooks.map((h, idx) => (idx === i ? { ...h, ...patch } : h)));
  };

  const remove = (i: number) => {
    onChange(hooks.filter((_, idx) => idx !== i));
  };

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between">
        <Label>Hooks</Label>
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <Plus />
          Add
        </Button>
      </div>
      {hooks.length === 0 && (
        <p className="text-xs text-muted-foreground">
          Pre/Post tool guards, approval gates, logging — pick a template, the daemon registers it
          on every session.
        </p>
      )}
      {hooks.map((h, i) => {
        const tpl = HOOK_TEMPLATES.find((t) => t.key === h.template_key);
        return (
          <div key={i} className="space-y-1.5 rounded border p-2">
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={h.template_key}
                onValueChange={(key) => {
                  const next = HOOK_TEMPLATES.find((t) => t.key === key);
                  if (!next) return;
                  update(i, {
                    template_key: key,
                    event: next.defaultEvent,
                    params: { ...next.defaultParams },
                  });
                }}
              >
                <SelectTrigger className="h-8 w-auto min-w-[180px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOOK_TEMPLATES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={h.event}
                onValueChange={(e) => update(i, { event: e as HookEvent })}
              >
                <SelectTrigger className="h-8 w-auto min-w-[140px] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOOK_EVENTS.map((ev) => (
                    <SelectItem key={ev} value={ev}>
                      {ev}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex-1" />
              <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}>
                <Trash2 />
              </Button>
            </div>
            {tpl && <p className="text-xs text-muted-foreground">{tpl.description}</p>}
            {Object.keys(h.params).length > 0 && (
              <Textarea
                value={JSON.stringify(h.params, null, 2)}
                onChange={(e) => {
                  try {
                    update(i, { params: JSON.parse(e.target.value) });
                  } catch {
                    /* keep typing */
                  }
                }}
                className="min-h-[60px] font-mono text-[11px]"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------------------- StartAgentDialog */

const MODELS: Array<{ value: string; label: string }> = [
  { value: 'claude-opus-4-7', label: 'claude-opus-4-7' },
  { value: 'claude-opus-4-6', label: 'claude-opus-4-6' },
  { value: 'claude-sonnet-4-6', label: 'claude-sonnet-4-6' },
  { value: 'claude-haiku-4-5', label: 'claude-haiku-4-5' },
];

function StartAgentDialog({
  agent,
  onOpenChange,
  onStarted,
}: {
  agent: Agent | null;
  onOpenChange: (open: boolean) => void;
  onStarted: (sessionId: string) => void;
}) {
  const [initialMessage, setInitialMessage] = useState('');
  const [advanced, setAdvanced] = useState(false);
  const [model, setModel] = useState('');
  const [permMode, setPermMode] = useState<'autoAccept' | 'askUser' | ''>('');
  const [overridePrompt, setOverridePrompt] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [overrideSkills, setOverrideSkills] = useState(false);
  const [skillIds, setSkillIds] = useState<string[]>([]);
  const [overrideMcps, setOverrideMcps] = useState(false);
  const [mcpIds, setMcpIds] = useState<string[]>([]);
  const [allSkills, setAllSkills] = useState<SkillRef[]>([]);
  const [allMcps, setAllMcps] = useState<McpRef[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!agent) return;
    setInitialMessage('');
    setAdvanced(false);
    setModel('');
    setPermMode('');
    setOverridePrompt(false);
    setPrompt(agent.inlineSystemPrompt ?? '');
    setOverrideSkills(false);
    const baseSkills = new Set(agent.additionalSkillIds ?? []);
    if (agent.primarySkillId) baseSkills.add(agent.primarySkillId);
    setSkillIds([...baseSkills]);
    setOverrideMcps(false);
    setMcpIds(agent.mcpIds ?? []);
    setErr(null);
    void api.get<SkillRef[]>('/api/skills').then(setAllSkills).catch(() => undefined);
    void api.get<McpRef[]>('/api/mcps').then(setAllMcps).catch(() => undefined);
  }, [agent]);

  if (!agent) return null;

  const start = async () => {
    setBusy(true);
    setErr(null);
    try {
      const overrides: Record<string, unknown> = {};
      if (model) overrides['model'] = model;
      if (permMode) overrides['permission_mode'] = permMode;
      if (overridePrompt) overrides['inline_system_prompt'] = prompt;
      if (overrideSkills) overrides['skill_ids'] = skillIds;
      if (overrideMcps) overrides['mcp_ids'] = mcpIds;
      const body: Record<string, unknown> = {};
      if (initialMessage.trim()) body['initial_user_message'] = initialMessage;
      if (Object.keys(overrides).length > 0) body['overrides'] = overrides;
      const r = await api.post<{ session: { id: string } }>(
        `/api/agents/${agent.id}/start`,
        body,
      );
      onStarted(r.session.id);
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (set: React.Dispatch<React.SetStateAction<string[]>>) => (id: string) =>
    set((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open={agent !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Start "{agent.name}"</DialogTitle>
        </DialogHeader>
        <div className="max-h-[70vh] space-y-3 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="start-msg">Initial user message (optional)</Label>
            <Textarea
              id="start-msg"
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder="e.g. Audit the deploy script and tell me what could fail."
              className="min-h-[80px]"
            />
          </div>

          <div className="flex items-center justify-between gap-3 rounded-md border p-3">
            <div>
              <Label htmlFor="start-advanced" className="cursor-pointer">
                Override agent settings
              </Label>
              <p className="text-xs text-muted-foreground">
                One-shot tweaks for this session only — agent template stays unchanged.
              </p>
            </div>
            <Switch id="start-advanced" checked={advanced} onCheckedChange={setAdvanced} />
          </div>

          {advanced && (
            <div className="space-y-3 rounded-md border p-3">
              <div className="space-y-1.5">
                <Label>Model</Label>
                <Select
                  value={model || agent.model}
                  onValueChange={(v) => setModel(v === agent.model ? '' : v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                        {m.value === agent.model && (
                          <span className="ml-2 text-xs text-muted-foreground">(agent default)</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Permission mode</Label>
                <Select
                  value={permMode || agent.permissionMode}
                  onValueChange={(v) =>
                    setPermMode(v === agent.permissionMode ? '' : (v as 'autoAccept' | 'askUser'))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="askUser">askUser</SelectItem>
                    <SelectItem value="autoAccept">autoAccept</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="start-prompt">Override system prompt</Label>
                  <Switch
                    id="start-prompt"
                    checked={overridePrompt}
                    onCheckedChange={setOverridePrompt}
                  />
                </div>
                {overridePrompt && (
                  <Textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    className="min-h-[100px]"
                  />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="start-skills">Override skills</Label>
                  <Switch
                    id="start-skills"
                    checked={overrideSkills}
                    onCheckedChange={setOverrideSkills}
                  />
                </div>
                {overrideSkills && (
                  <PickerList
                    items={allSkills}
                    selectedIds={skillIds}
                    onToggle={toggle(setSkillIds)}
                    empty="No skills."
                    renderRow={(s) => (
                      <div className="min-w-0">
                        <div className="truncate font-medium">{s.name}</div>
                        {s.description && (
                          <p className="truncate text-xs text-muted-foreground">{s.description}</p>
                        )}
                      </div>
                    )}
                  />
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="start-mcps">Override MCPs</Label>
                  <Switch
                    id="start-mcps"
                    checked={overrideMcps}
                    onCheckedChange={setOverrideMcps}
                  />
                </div>
                {overrideMcps && (
                  <PickerList
                    items={allMcps}
                    selectedIds={mcpIds}
                    onToggle={toggle(setMcpIds)}
                    empty="No MCPs."
                    renderRow={(m) => (
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate font-medium">{m.name}</span>
                          <Badge variant="outline" className="shrink-0 text-[10px]">{m.kind}</Badge>
                        </div>
                      </div>
                    )}
                  />
                )}
              </div>
            </div>
          )}

          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={start}>
            <Play />
            Start
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----------------------------------------------------------------- CwdField */

/**
 * Cwd field. Always free-text editable; when the chosen sandbox has a
 * non-empty `workingDirs` allowlist, a quick-pick Select sits above the
 * input. Manual entry outside the allowlist is allowed but flagged.
 */
function CwdField({
  sandboxId,
  sandboxes,
  value,
  onChange,
}: {
  sandboxId: string;
  sandboxes: Sandbox[];
  value: string;
  onChange: (next: string) => void;
}) {
  const sb = sandboxes.find((s) => s.id === sandboxId);
  const dirs = sb?.workingDirs ?? [];
  const hasAllowlist = dirs.length > 0;
  const inAllowlist = hasAllowlist && dirs.includes(value);
  const customSelected = hasAllowlist && value !== '' && !inAllowlist;

  return (
    <div className="space-y-1.5">
      <Label htmlFor="ag-cwd">Working directory</Label>

      {hasAllowlist && (
        <Select
          value={inAllowlist ? value : customSelected ? '__custom__' : ''}
          onValueChange={(v) => {
            if (v === '__custom__') return; // already in custom mode
            onChange(v);
          }}
        >
          <SelectTrigger className="text-xs">
            <SelectValue placeholder="Pick from allowlist" />
          </SelectTrigger>
          <SelectContent>
            {dirs.map((d) => (
              <SelectItem key={d} value={d}>
                <span className="font-mono text-xs">{d}</span>
              </SelectItem>
            ))}
            <SelectItem value="__custom__">
              <span className="text-xs italic">Custom path…</span>
            </SelectItem>
          </SelectContent>
        </Select>
      )}

      <Input
        id="ag-cwd"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hasAllowlist ? 'or type a custom path' : '/tmp/control-agent'}
        className="font-mono text-xs"
      />

      {customSelected && (
        <p className="text-[11px] text-amber-500">
          <code>{value}</code> isn't in this sandbox's allowlist — agent will run there anyway.
        </p>
      )}
    </div>
  );
}
